import React, { useState } from "react";
import axios from "axios";
import "./Login.css";

const API_BASE = "/api";

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Two-step login for staff: set after the password step.
  const [mfaToken, setMfaToken] = useState("");
  const [mfaSetup, setMfaSetup] = useState(null);
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [pendingLogin, setPendingLogin] = useState(null);

  const resetToPassword = (message = "") => {
    setMfaToken("");
    setMfaSetup(null);
    setCode("");
    setPassword("");
    setError(message);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!email.trim() || !password) {
      setError("Enter your email address and password.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const response = await axios.post(`${API_BASE}/auth/login`, {
        email: email.trim().toLowerCase(),
        password,
      });

      if (response.data?.mfaRequired) {
        setMfaToken(response.data.mfaToken);

        if (response.data.mfaSetupRequired) {
          const setup = await axios.post(`${API_BASE}/auth/mfa/setup`, {
            mfaToken: response.data.mfaToken,
          });
          setMfaSetup(setup.data);
        }

        return;
      }

      if (!response.data?.token || !response.data?.user) {
        throw new Error("Invalid login response");
      }

      onLogin(response.data);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Unable to sign in. Please check your credentials."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCode = async (event) => {
    event.preventDefault();

    if (!code.trim()) {
      setError("Enter the code from your authenticator app.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const response = await axios.post(`${API_BASE}/auth/mfa/verify`, {
        mfaToken,
        code: code.trim(),
      });

      // First setup: show the recovery codes once before going in.
      if (response.data.recoveryCodes) {
        setRecoveryCodes(response.data.recoveryCodes);
        setPendingLogin(response.data);
        return;
      }

      onLogin(response.data);
    } catch (err) {
      if (err.response?.status === 401 && !err.response?.data?.error?.includes("code")) {
        resetToPassword(err.response.data.error);
      } else {
        setError(err.response?.data?.error || "Unable to check the code.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (recoveryCodes) {
    return (
      <main className="login-page">
        <div className="login-card">
          <h1>Save your recovery codes</h1>
          <p>Two-step login is on.</p>
          <p className="login-help">
            If you lose your phone, each of these codes signs you in once
            instead of an authenticator code. Keep them somewhere safe; they
            won't be shown again.
          </p>
          <ul className="login-codes">
            {recoveryCodes.map((recoveryCode) => (
              <li key={recoveryCode}>{recoveryCode}</li>
            ))}
          </ul>
          <button type="button" onClick={() => onLogin(pendingLogin)}>
            I've saved them, continue
          </button>
        </div>
      </main>
    );
  }

  if (mfaToken) {
    return (
      <main className="login-page">
        <form className="login-card" onSubmit={handleCode}>
          <h1>Two-step login</h1>
          <p>ReachLink</p>

          {error && <div className="login-error">{error}</div>}

          {mfaSetup ? (
            <>
              <p className="login-help">
                Staff accounts need a code from an authenticator app (such as
                Google Authenticator, Microsoft Authenticator or 1Password).
                Scan this with the app, then enter the 6-digit code it shows.
              </p>
              <img className="login-qr" src={mfaSetup.qrCode} alt="Authenticator setup code" />
              <p className="login-help">Can't scan? Enter this key in the app:</p>
              <div className="login-secret">{mfaSetup.secret}</div>
            </>
          ) : (
            <p className="login-help">
              Enter the 6-digit code from your authenticator app, or one of
              your recovery codes.
            </p>
          )}

          <label htmlFor="login-code">Code</label>
          <input
            id="login-code"
            inputMode={mfaSetup ? "numeric" : "text"}
            autoComplete="one-time-code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            disabled={submitting}
            autoFocus
          />

          <button type="submit" disabled={submitting}>
            {submitting ? "Checking..." : "Continue"}
          </button>
          <button
            type="button"
            className="login-link-button"
            onClick={() => resetToPassword()}
            disabled={submitting}
          >
            Back
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="login-page">
      <form className="login-card" onSubmit={handleSubmit}>
        <h1 className="login-logo">
          <img src="/reachlink-logo.png" alt="ReachLink" />
        </h1>
        <p>Business texting by 10 Digits</p>

        {error && <div className="login-error">{error}</div>}

        <label htmlFor="login-email">Email address</label>
        <input
          id="login-email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={submitting}
        />

        <label htmlFor="login-password">Password</label>
        <input
          id="login-password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          disabled={submitting}
        />

        <button type="submit" disabled={submitting}>
          {submitting ? "Signing in..." : "Sign in"}
        </button>
        <p className="login-help" style={{ marginTop: 18 }}>
          Forgot your password? Ask your administrator for a reset link.
        </p>
      </form>
    </main>
  );
}

export default Login;
